import type { MemoizedSelector } from '@ngrx/store';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';

import type { CachedImage } from '../../../databases/app.db';
import type { HuntingProductGalleryMedia } from '../../models/hunting-product-gallery-media.interface';
import type { HuntingProductMedia, SyncState } from '../../models/hunting-product-media.model';
import { BackendStateValues, hasShotSuffix, parseCapturedAtFromFileName, SyncStateValues } from '../../models/hunting-product-media.model';
import { VideoStatusValues } from '../../models/video-status.model';
import { createScopedSelector } from '../../utils/factory.utils';
import { HUNTING_PRODUCT_MEDIA_ROOT } from '../../services/file.service';
import { HuntingProductsActions } from '../../../pages/hunting-products/redux/hunting-products.actions';

import { HuntingProductSyncActions } from './hunting-product-sync.actions';
import type { HuntingProductDetailsMediaPreviewItem } from './hunting-product-details-media-preview-item.interface';
import type { HuntingProductGalleryItem } from './hunting-product-gallery-item.interface';
import type { HuntingProductGallery } from './hunting-product-gallery.interface';
import type { HuntingProductTransferRatio } from './hunting-product-transfer-ratio.interface';

export const HUNTING_PRODUCT_SYNC_STORE_KEY = 'huntingProductSync';

export type SyncCycleState = 'idle' | 'discovering' | 'transferring' | 'succeeded' | 'failed';

function isActiveCycleState(cycleState: SyncCycleState): boolean {
  return cycleState === 'discovering' || cycleState === 'transferring';
}

export interface TransferProgress {
  id: string;
  bytes: number;
  contentLength: number;
  mbPerSecWindow: number;
}

export interface FailedMediaItem {
  id: string;
  error: string;
}

export interface HuntingProductSyncState {
  cycleState: SyncCycleState;
  syncedCount: number;
  pendingCount: number;
  lastError: string | undefined;
  transfer: TransferProgress | undefined;
  failed: Array<FailedMediaItem>;
  // Every id discovery found for this cycle, so the gallery can lay the whole run out at once
  // instead of growing a tile per started transfer. Set once per cycle and dropped when it ends —
  // an id that outlived its cycle would keep a tile on screen for a file nobody is fetching.
  pendingIds: Array<string>;
  // Which of pendingIds actually landed — syncedCount cannot answer that, and syncGalleryFailed needs the
  // ids to tell the files the broken-off cycle still owes from the ones it already delivered.
  syncedIds: Array<string>;
  // The id in flight when cancelSync fired; its mediaItem* actions can still arrive (no abort API).
  // Reset on the next syncDiscovery so that cycle may retry the id.
  cancelledTransferId: string | null;
  media: Array<HuntingProductGalleryMedia>;
  // The device the stored media belongs to. A listing that resolves after the user moved on to another
  // device must not overwrite it.
  mediaSerial: string | undefined;
  openedMediaId: string | null;
}

const initialState: HuntingProductSyncState = {
  cycleState: 'idle',
  syncedCount: 0,
  pendingCount: 0,
  lastError: undefined,
  transfer: undefined,
  failed: [],
  pendingIds: [],
  syncedIds: [],
  cancelledTransferId: null,
  media: [],
  mediaSerial: undefined,
  openedMediaId: null,
};

// Registered at root (app.config.ts) and again in the BLE route branch; the fallback only serves a
// store without the slice, as in the selector specs.
const selectHuntingProductSyncFeature = createFeatureSelector<HuntingProductSyncState | undefined>(HUNTING_PRODUCT_SYNC_STORE_KEY);
const selectHuntingProductSyncState = createSelector(selectHuntingProductSyncFeature, state => state ?? initialState);
const createHuntingProductSyncSelector = createScopedSelector(selectHuntingProductSyncState);

function outstandingIds(state: HuntingProductSyncState): Array<string> {
  const settledIds = new Set([...state.syncedIds, ...state.failed.map(item => item.id)]);
  return state.pendingIds.filter(id => !settledIds.has(id));
}

export const huntingProductSyncReducer = createReducer(
  initialState,

  on(
    HuntingProductSyncActions.syncDiscovery,
    (state): HuntingProductSyncState => ({
      ...state,
      cycleState: 'discovering',
      syncedCount: 0,
      pendingCount: 0,
      lastError: undefined,
      transfer: undefined,
      failed: [],
      pendingIds: [],
      syncedIds: [],
      cancelledTransferId: null,
    })
  ),

  on(
    HuntingProductSyncActions.syncDiscoverySucceeded,
    (state, { pendingCount, pendingIds }): HuntingProductSyncState => ({
      ...state,
      cycleState: 'transferring',
      pendingCount,
      pendingIds,
    })
  ),

  on(
    HuntingProductSyncActions.syncDiscoveryFailed,
    (state, { error }): HuntingProductSyncState => ({
      ...state,
      cycleState: 'failed',
      lastError: error,
    })
  ),

  // A started transfer may only take the slot while it stands empty, or while it already belongs to
  // the same id (transferSingleItem re-emits this per retry attempt) — see mediaItemSyncProgress below
  // for why the id alone cannot be trusted. Every path that begins a cycle or ends an item clears
  // the slot, so an occupied one always means a different, currently-live transfer.
  on(
    HuntingProductSyncActions.mediaItemSync,
    (state, { id, contentLength }): HuntingProductSyncState =>
      id === state.cancelledTransferId || (state.transfer !== undefined && state.transfer.id !== id)
        ? state
        : { ...state, transfer: { id, bytes: 0, contentLength, mbPerSecWindow: 0 } }
  ),

  // Gated on the current transfer's id, not cancelledTransferId: that is reset on the next
  // syncDiscovery, but the cancelled item's native download can still emit for the same id in a later
  // cycle. Matching transfer.id means a stale event is only ever rejected.
  on(
    HuntingProductSyncActions.mediaItemSyncProgress,
    (state, { id, bytes, contentLength, mbPerSecWindow }): HuntingProductSyncState =>
      id === state.transfer?.id ? { ...state, transfer: { id, bytes, contentLength, mbPerSecWindow } } : state
  ),

  // Not id-gated: a file finishing after cancel is kept (bandwidth spent, the directory is the
  // truth) and counts as synced; getSyncProgress keeps it off the cancelled run's status line.
  // `transfer` is cleared only if it is this item's, so a late zombie cannot blank a live transfer.
  on(
    HuntingProductSyncActions.mediaItemSyncSucceeded,
    (state, { media }): HuntingProductSyncState => ({
      ...state,
      syncedCount: state.syncedCount + 1,
      syncedIds: [...state.syncedIds, media.id],
      transfer: state.transfer?.id === media.id ? undefined : state.transfer,
      // The projection puts `error` ahead of the on-disk state, and only syncDiscovery clears the list.
      failed: state.failed.filter(item => item.id !== media.id),
    })
  ),

  on(
    HuntingProductSyncActions.mediaItemSyncFailed,
    (state, { id, error }): HuntingProductSyncState =>
      id === state.cancelledTransferId
        ? state
        : { ...state, transfer: state.transfer?.id === id ? undefined : state.transfer, failed: [...state.failed, { id, error }] }
  ),

  on(
    HuntingProductSyncActions.syncGallerySucceeded,
    (state, { syncedCount }): HuntingProductSyncState => ({
      ...state,
      cycleState: 'succeeded',
      syncedCount,
      transfer: undefined,
      pendingIds: [],
    })
  ),

  // The files the cycle never got to are failed too, under the cycle's own error: nothing else
  // reports them (only a transfer that starts and then breaks emits mediaItemSyncFailed), and without an
  // entry here their ids leave with pendingIds and their tiles disappear unannounced.
  // cancelSync below is deliberately not the same case — the user stopping the run is not an error.
  on(
    HuntingProductSyncActions.syncGalleryFailed,
    (state, { error }): HuntingProductSyncState => ({
      ...state,
      cycleState: 'failed',
      lastError: error,
      transfer: undefined,
      pendingIds: [],
      failed: [...state.failed, ...outstandingIds(state).map(id => ({ id, error }))],
    })
  ),

  // Only the queue stops; the running native transfer cannot be aborted and its result is kept
  // (see mediaItemSyncSucceeded).
  on(
    HuntingProductSyncActions.cancelSync,
    (state): HuntingProductSyncState => ({
      ...state,
      cycleState: 'idle',
      pendingCount: 0,
      pendingIds: [],
      transfer: undefined,
      cancelledTransferId: state.transfer?.id ?? null,
    })
  ),

  on(
    HuntingProductSyncActions.loadMedia,
    (state, { serial }): HuntingProductSyncState => ({
      ...state,
      mediaSerial: serial,
      media: serial === state.mediaSerial ? state.media : [],
    })
  ),

  on(
    HuntingProductSyncActions.loadMediaSucceeded,
    (state, { serial, media }): HuntingProductSyncState => (serial === state.mediaSerial ? { ...state, media } : state)
  ),

  on(HuntingProductSyncActions.loadMediaItemSucceeded, (state, { media }): HuntingProductSyncState => {
    if (media.serial !== state.mediaSerial) return state;
    return state.media.some(item => item.id === media.id)
      ? { ...state, media: state.media.map(item => (item.id === media.id ? media : item)) }
      : { ...state, media: [...state.media, media] };
  }),

  on(HuntingProductSyncActions.galleryVideoSelected, (state, { mediaId }): HuntingProductSyncState => ({ ...state, openedMediaId: mediaId })),

  on(
    HuntingProductSyncActions.galleryVideoViewerDismissed,
    HuntingProductsActions.cannesGalleryPageLeft,
    (state): HuntingProductSyncState => ({ ...state, openedMediaId: null })
  ),

  on(HuntingProductSyncActions.localMediaCleared, (): HuntingProductSyncState => initialState)
);

// The id is `{serial}/{yearMonth}/{fileName}`. A multi-segment serial only grows the string to the
// left — yearMonth and fileName are always exactly the last two segments — so splitting from the
// right finds both without needing the serial as an input, unlike parseMediaItemId().
function splitMediaId(id: string): { serial: string; fileName: string } {
  const segments = id.split('/');
  return {
    // `?? ''` satisfies the type only; split() never yields an empty array.
    fileName: segments.at(-1) ?? '',
    serial: segments.slice(0, -2).join('/'),
  };
}

// A transfer or a just-failed item may not exist on disk yet: iOS's URLSessionDownloadTask writes to
// the OS's own temp location and moves the file to the target path only once the download completes,
// so nothing appears under HUNTING_PRODUCT_MEDIA_ROOT for the whole transfer — unlike Android, where the
// partial file is visible from the first byte. Without this stand-in, no in-flight tile ever renders on iOS.
function buildPlaceholderMedia(id: string): HuntingProductMedia {
  const { serial, fileName } = splitMediaId(id);
  return {
    id,
    serial,
    capturedAt: parseCapturedAtFromFileName(fileName),
    type: fileName.startsWith('vid_') ? 'video' : 'image',
    localFilePath: `${HUNTING_PRODUCT_MEDIA_ROOT}/${id}`,
    thumbnailPath: null,
    syncState: SyncStateValues.PENDING,
    backendState: BackendStateValues.LOCAL,
    uploadedAt: null,
  };
}

/**
 * `originalImage.fileName`/`video.fileName` stay Directory.Data-relative: resolving them needs the async Filesystem plugin,
 * which a pure selector cannot call — the tile resolves per item. `id` stays the sync pipeline's
 * dedup key.
 */
function toCachedImage(media: HuntingProductMedia, syncState: SyncState, customerId: string): CachedImage {
  return {
    id: media.id,
    metadataVersion: 2,
    cameraId: media.serial,
    loggedInCustomerId: customerId,
    takenAt: new Date(media.capturedAt).toISOString(),
    updatedAt: new Date(media.capturedAt).toISOString(),
    originalImage: { fileName: media.localFilePath, width: 0, height: 0, aspectRatio: '' },
    isFavorite: false,
    hasColorizedImage: false,
    legacyUrls: { original: media.localFilePath },
    tags: [],
    aspectRatio: null,
    blurDataUrl: null,
    ...(media.type === 'video'
      ? {
          video: {
            fileName: media.localFilePath,
            status: syncState === SyncStateValues.COMPLETE ? VideoStatusValues.DOWNLOADED : VideoStatusValues.REQUESTED,
          },
        }
      : {}),
  };
}

/**
 * Overlays the on-disk listing with NgRx sync state; ids not on disk yet (in-flight, failed,
 * pending) get a placeholder. `media`/`customerId` are parameters because neither lives in the
 * store. Precedence: error > in-flight > disk state. Only the transfer's id enters, never byte
 * counters, so progress ticks cannot re-sort the list.
 */
export function projectHuntingProductGalleryItems(
  media: Array<HuntingProductMedia & Partial<Pick<HuntingProductGalleryMedia, 'displaySrc' | 'sizeBytes'>>>,
  transferId: string | undefined,
  failed: Array<FailedMediaItem>,
  pendingIds: Array<string>,
  customerId: string
): Array<HuntingProductGalleryItem> {
  const errorById = new Map(failed.map(({ id, error }) => [id, error]));
  const byId = new Map(media.map(item => [item.id, item]));

  if (transferId !== undefined && !byId.has(transferId)) {
    byId.set(transferId, buildPlaceholderMedia(transferId));
  }
  for (const id of errorById.keys()) {
    if (!byId.has(id)) {
      byId.set(id, buildPlaceholderMedia(id));
    }
  }
  for (const id of pendingIds) {
    if (!byId.has(id)) {
      byId.set(id, buildPlaceholderMedia(id));
    }
  }

  return [...byId.values()].map(item => {
    const error = errorById.get(item.id);
    const isTransferring = transferId === item.id;

    const syncState: SyncState = error === undefined ? (isTransferring ? SyncStateValues.IN_PROGRESS : item.syncState) : SyncStateValues.FAILED;

    return {
      image: toCachedImage(item, syncState, customerId),
      error: error ?? null,
      syncState,
      isVideo: item.type === 'video',
      // Read from `id`, not the file name: a leftover staging file gives the file name a `.partial-<cycleId>` suffix,
      // which hides the device's `_shot`/`_preshot` suffix.
      isShotCapture: hasShotSuffix(item.id),
      displaySrc: item.displaySrc,
      sizeBytes: item.sizeBytes,
    };
  });
}

export function isPlayableGalleryVideo(item: HuntingProductGalleryItem): boolean {
  return item.isVideo && !item.isShotCapture && item.syncState === SyncStateValues.COMPLETE && item.displaySrc !== undefined;
}

const MEDIA_PREVIEW_LIMIT = 3;

const selectMedia = createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.media);
const selectTransfer = createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.transfer);
const selectTransferId = createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.transfer?.id);
const selectFailed = createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.failed);
const selectPendingIds = createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.pendingIds);
const selectMediaSerial = createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.mediaSerial);
const selectOpenedMediaId = createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.openedMediaId);

// The sync slice is global, so what belongs to another device must not surface next to this device's media.
function belongsToSerial(id: string, serial: string | undefined): boolean {
  return serial !== undefined && splitMediaId(id).serial === serial;
}

const selectTransferIdForMedia = createSelector(selectTransferId, selectMediaSerial, (transferId, serial) =>
  transferId !== undefined && belongsToSerial(transferId, serial) ? transferId : undefined
);
const selectFailedForMedia = createSelector(selectFailed, selectMediaSerial, (failed, serial) => failed.filter(({ id }) => belongsToSerial(id, serial)));
const selectPendingIdsForMedia = createSelector(selectPendingIds, selectMediaSerial, (pendingIds, serial) =>
  pendingIds.filter(id => belongsToSerial(id, serial))
);

// Keyed on the transfer id, not the transfer, so progress ticks keep the result's identity. Each call
// starts with an empty memo: create the selector once and keep it.
// Newest first by takenAt; unreliable device clocks stay visible rather than being papered over with a "today" fallback.
function createGalleryItemsSelector(customerId: string): MemoizedSelector<object, Array<HuntingProductGalleryItem>> {
  return createSelector(selectMedia, selectTransferIdForMedia, selectFailedForMedia, selectPendingIdsForMedia, (media, transferId, failed, pendingIds) =>
    projectHuntingProductGalleryItems(media, transferId, failed, pendingIds, customerId).sort(
      (a, b) => new Date(b.image.takenAt).getTime() - new Date(a.image.takenAt).getTime()
    )
  );
}

export const HuntingProductSyncSelectors = {
  getCycleState: createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.cycleState),
  isSyncing: createHuntingProductSyncSelector((state: HuntingProductSyncState) => isActiveCycleState(state.cycleState)),
  getSyncedCount: createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.syncedCount),
  getPendingCount: createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.pendingCount),
  getLastError: createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.lastError),
  getTransfer: selectTransfer,
  getTransferProgress: createSelector(selectTransfer, (transfer): HuntingProductTransferRatio | undefined =>
    transfer
      ? { id: transfer.id, ratio: transfer.contentLength > 0 ? transfer.bytes / transfer.contentLength : null, contentLength: transfer.contentLength }
      : undefined
  ),
  getMedia: selectMedia,
  getMediaSerial: createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.mediaSerial),
  getMediaPreview: createSelector(
    selectMedia,
    (media): Array<HuntingProductDetailsMediaPreviewItem> =>
      media
        .flatMap(item =>
          item.type === 'image' && item.syncState === SyncStateValues.COMPLETE && item.displaySrc !== undefined
            ? [{ id: item.id, src: item.displaySrc, capturedAt: item.capturedAt }]
            : []
        )
        .sort((a, b) => b.capturedAt - a.capturedAt)
        .slice(0, MEDIA_PREVIEW_LIMIT)
        .map(({ id, src }) => ({ id, src }))
  ),
  getFailed: selectFailed,
  getFailedForMedia: selectFailedForMedia,
  getCancelledTransferId: createHuntingProductSyncSelector((state: HuntingProductSyncState) => state.cancelledTransferId),
  getOpenedVideoItemFor: (customerId: string) =>
    createSelector(createGalleryItemsSelector(customerId), selectOpenedMediaId, (items, openedMediaId): HuntingProductGalleryItem | null => {
      const item = items.find(({ image }) => image.id === openedMediaId);
      return item && isPlayableGalleryVideo(item) ? item : null;
    }),
  // "Synchronizing x of y items" — x/y, not the finished string; the string is Transloco's job.
  // Gated on an active cycle: a file finishing after cancelSync still bumps syncedCount, but the
  // cancelled run's status line must stay off.
  getSyncProgress: createHuntingProductSyncSelector((state: HuntingProductSyncState): { synced: number; total: number } => {
    if (!isActiveCycleState(state.cycleState)) {
      return { synced: 0, total: 0 };
    }
    // pendingCount is what discovery found and is never decremented, so it already IS the total.
    return { synced: state.syncedCount, total: state.pendingCount };
  }),
  getGalleryItemsFor: createGalleryItemsSelector,
  getGalleryFor: (customerId: string) =>
    createSelector(
      createGalleryItemsSelector(customerId),
      (items): HuntingProductGallery => ({
        images: items.map(item => item.image),
        itemById: new Map(items.map(item => [item.image.id, item])),
      })
    ),
};
